import { getActiveAccessToken } from '@/features/impersonation-session';
import { requireEnv } from '@/lib/env';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $fetchAllo } from '@/services/api-allo/client';

const BASE_URL = import.meta.env.VITE_API_ALLO_URL;

requireEnv('VITE_API_ALLO_URL', BASE_URL);

type ErrorPayload = {
  code?: string;
};

type InternalUsersResponse = {
  data: Array<{
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    account_type: string | null;
  }>;
  metadata?: {
    pagination?: {
      current_page?: number;
      total_pages?: number;
      total_elements?: number;
    };
  };
};

async function authenticatedFetch(path: string, init?: RequestInit) {
  const accessToken = await getActiveAccessToken();
  if (!accessToken) {
    throw new Error('UNAUTHORIZED');
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ErrorPayload | null;
    throw new Error(payload?.code ?? 'REQUEST_FAILED');
  }

  return response;
}

export async function getInternalUsers({
  page,
  search,
  size = 25,
}: {
  page: number;
  search?: string;
  size?: number;
}) {
  const params = new URLSearchParams({
    'page[number]': String(page),
    'page[size]': String(size),
  });

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  const response = await authenticatedFetch(`/v1/admin/internal/users?${params.toString()}`);
  return (await response.json()) as InternalUsersResponse;
}

export async function impersonateUser(userId: string) {
  const response = await authenticatedFetch(`/v1/admin/user/${userId}/impersonate`, {
    method: 'POST',
  });

  const payload = (await response.json()) as { data: string };
  return payload.data;
}

// --- OAuth App Management ---

export type OAuthApp = Required<
  Pick<
    components['schemas']['OAuthApp'],
    'pid' | 'name' | 'client_id' | 'redirect_uris' | 'scopes' | 'status' | 'visibility'
  >
> &
  Omit<
    components['schemas']['OAuthApp'],
    'pid' | 'name' | 'client_id' | 'redirect_uris' | 'scopes' | 'status' | 'visibility'
  >;

export type OAuthAppRegistration = {
  app: OAuthApp;
  client_secret: string;
};

export async function getOAuthApps(): Promise<OAuthApp[]> {
  const { data } = await $fetchAllo.GET('/v1/admin/oauth/apps');
  return (data ?? []) as OAuthApp[];
}

export async function createOAuthApp(
  body: components['schemas']['OAuthAppCreateRequest'],
): Promise<OAuthAppRegistration> {
  const { data } = await $fetchAllo.POST('/v1/admin/oauth/apps', { body });
  return data as unknown as OAuthAppRegistration;
}

export async function updateOAuthApp(
  pid: string,
  body: components['schemas']['OAuthAppUpdateRequest'],
) {
  const { data } = await $fetchAllo.PATCH('/v1/admin/oauth/apps/{pid}', {
    params: { path: { pid } },
    body,
  });
  return data!;
}

export async function approveOAuthApp(pid: string, note?: string) {
  const { data } = await $fetchAllo.POST('/v1/admin/oauth/apps/{pid}/approve', {
    params: { path: { pid } },
    body: { note },
  });
  return data!;
}

export async function rejectOAuthApp(pid: string, note: string) {
  const { data } = await $fetchAllo.POST('/v1/admin/oauth/apps/{pid}/reject', {
    params: { path: { pid } },
    body: { note },
  });
  return data!;
}

export async function suspendOAuthApp(pid: string, note: string) {
  const { data } = await $fetchAllo.POST('/v1/admin/oauth/apps/{pid}/suspend', {
    params: { path: { pid } },
    body: { note },
  });
  return data!;
}
